import { Immutable } from "immer";
import React, { useMemo } from "react";
import { Link } from "tsi-common-react/src/components/Link/index";
import { RichText } from "tsi-common-react/src/components/RichText/index";
import { SVGIcon } from "tsi-common-react/src/components/SVGIcon/index";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { Cms, Context } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { MainMenu } from "../MainMenu";
import { IMenuItem } from "../MainMenu/menu.interface";

type PromoHeaderBlockProps = Immutable<{
    id?: string;
    type?: "promo";
    index?: number;
    value: Cms | null;
}>;

/**
 * PromoHeaderBlock component renders promotional content in the site header
 * Handles both desktop and mobile variants of promo copy
 */
const PromoHeaderBlock = ({
    value,
}: PromoHeaderBlockProps): React.ReactNode => {
    // Simple fallback for mobile copy
    const mobileCopy = value?.mobile_copy || value?.copy || "";
    if (!value) {
        return null;
    }
    return (
        <section>
            <div
                className="search-nav__promo"
                role="region"
                aria-label="Desktop promotional banner"
            >
                <RichText html={value.copy ?? ""} />
            </div>
            <div
                className="search-nav__promo search-nav__promo--mobile"
                role="region"
                aria-label="Mobile promotional banner"
            >
                <RichText html={mobileCopy} />
            </div>
        </section>
    );
};

type IProps = React.ContextType<typeof Context>["chrome"]["site_header"];

export const SiteHeader = (props: IProps) => {
    const homeURL = usePageURL("home");
    const { promo_settings, top_nav_menu } = props;

    // Performance: Memoize menu items to prevent unnecessary recalculations during re-renders
    const menuItems: IMenuItem[] = useMemo(
        () =>
            Array.isArray(top_nav_menu?.menu?.value)
                ? (top_nav_menu.menu.value as IMenuItem[])
                : [],
        [top_nav_menu?.menu?.value],
    );

    return (
        <>
            {/* Landing page intro overlay mount point - can be evaluated during landing page migration */}
            <div id="intro-promo-holder" />
            <header
                id="site-nav-header"
                className="l-full-width site-nav-container"
                role="banner"
            >
                <nav
                    className="site-nav"
                    role="navigation"
                    aria-label="Main navigation"
                >
                    <div className="site-nav__inner-container">
                        <div className="site-nav__wrapper">
                            {/* Accessibility: Skip link for keyboard users */}
                            <a
                                className="site-nav__screen-reader-skip"
                                href="#main-content"
                            >
                                {t`Skip to main content`}
                            </a>

                            {/* Desktop navigation */}
                            <div className="site-nav-holder">
                                <Link
                                    className="site-nav__logo-link al-site-nav__logo"
                                    title={t`Tempurpedic Toppers Home`}
                                    href={homeURL}
                                    aria-label={t`Tempur-Pedic Toppers Home`}
                                >
                                    <SVGIcon
                                        name="tempurToppersLogo"
                                        svgClassName="site-nav__logo"
                                        alt={t`Tempur-Pedic Toppers Logo`}
                                    />
                                </Link>
                                <div className="site-nav__main-menu">
                                    <MainMenu menuItems={menuItems} />
                                </div>
                            </div>

                            {/* Mobile navigation */}
                            <div className="site-nav__content l-capped-width">
                                <Link
                                    className="site-nav__logo-link site-nav__logo-link--mobile al-site-nav__logo"
                                    title={t`Tempurpedic Toppers Home`}
                                    href={homeURL}
                                    aria-label={t`Tempur-Pedic Toppers Home`}
                                >
                                    <SVGIcon
                                        name="tempurToppersLogo"
                                        svgClassName="site-nav__logo-mobile"
                                        alt={t`Tempur-Pedic Toppers Logo`}
                                    />
                                </Link>
                            </div>
                        </div>
                    </div>
                </nav>
            </header>

            {/* Promotional header section */}
            {promo_settings?.promo_header && (
                <div className="promo-header l-full-width" role="complementary">
                    <div className="l-capped-width">
                        <StreamField
                            tagName={React.Fragment}
                            value={promo_settings.promo_header.value}
                            components={{
                                promo: PromoHeaderBlock,
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
};
